import IndigoServiceClient from 'communication/IndigoServiceClient';
import { CorporateActionsRequest } from 'widgets/corpActions/api/CorporateActionsRequest';
import { ApiRequestOptions } from 'communication/ApiCallInfo';
import { CorporateActionsResponse } from 'widgets/corpActions/api/CorporateActionsResponse';
import { corporateActionsSearch } from 'widgets/corpActions/api/CorporateActionsApi';
import { SymbolEventsRequest } from 'widgets/corpActions/api/SymbolEventsRequest';
import {
    CorpActionTypeHolidayValue,
    isCorpActionHolidayType,
} from 'widgets/corpActions/data/HolidayType';
import { SymbolEvent } from 'widgets/corpActions/data/SymbolEvent';
import {
    mapCorporateActionDto,
    mapHolidayDtosToEvents,
} from 'widgets/corpActions/data/mapping';
import { HolidaysResponse } from 'widgets/corpActions/api/HolidaysResponse';
import { HolidaysRequest } from 'widgets/corpActions/api/HolidaysRequest';
import { holidaysSearch } from 'widgets/corpActions/api/HolidaysApi';
import { SymbolEventsResponse } from 'widgets/corpActions/api/SymbolEventsResponse';
import { isIndigoApiError } from 'communication/IndigoApiError';

export async function symbolEventsSearch(
    indigoServiceClient: IndigoServiceClient,
    request: SymbolEventsRequest,
    options: ApiRequestOptions
): Promise<SymbolEventsResponse> {
    const {
        types,
        symbology,
        symbols,
        dateSearchType,
        limit,
        lookforwardPeriod,
        lookbackPeriod,
    } = request;

    const typesArr = types?.length ?? -1 > 0 ? types?.split(',') ?? [] : [];

    const results = await Promise.allSettled([
        buildCorpActionsSearch(),
        buildHolidaysSearch(),
    ]);

    const events: SymbolEvent[] = [];
    const corpActionsResult = results[0];
    const holidaysResult = results[1];
    const ret: SymbolEventsResponse = {
        request,
        events,
        missingSymbols: [],
        bbSymbols: [],
        errors: [],
    };

    if (corpActionsResult.status === 'fulfilled') {
        const corpActionsResponse = corpActionsResult.value;
        if (corpActionsResponse) {
            events.push(
                ...corpActionsResponse.corporateActions.map(
                    mapCorporateActionDto
                )
            );
            ret.missingSymbols = corpActionsResponse.missingSymbols;
            ret.bbSymbols = corpActionsResponse.bbSymbols;
        }
    } else {
        if (isIndigoApiError(corpActionsResult.reason)) {
            corpActionsResult.reason.message =
                'Error retrieving Corporate Actions: ' +
                corpActionsResult.reason.message;
        }
        ret.errors.push(corpActionsResult.reason);
    }

    if (holidaysResult.status === 'fulfilled') {
        const holidaysResponse = holidaysResult.value;
        if (holidaysResponse) {
            const holidayEvents = mapHolidayDtosToEvents(
                holidaysResult.value!.holidays
            );
            events.push(...holidayEvents);
        }
    } else {
        if (isIndigoApiError(holidaysResult.reason)) {
            holidaysResult.reason.message =
                'Error retrieving holidays: ' + holidaysResult.reason.message;
        }
        ret.errors.push(holidaysResult.reason);
    }

    return ret;

    function buildCorpActionsSearch(): Promise<
        CorporateActionsResponse | undefined
    > {
        const corporateActionTypes = typesArr.filter(
            (t) => !isCorpActionHolidayType(t)
        );

        const doCorpActionSearch =
            typesArr.length === 0 || corporateActionTypes.length > 0;

        if (!doCorpActionSearch) {
            return Promise.resolve(undefined);
        }

        const corpActionsRequest: CorporateActionsRequest = {
            symbols: symbols,
            symbology,
            dateSearchType,
            limit,
            lookforwardPeriod,
            lookbackPeriod,
            corpActTypes: corporateActionTypes.join(','),
        };

        return corporateActionsSearch(
            indigoServiceClient,
            corpActionsRequest,
            options
        );
    }

    function buildHolidaysSearch(): Promise<HolidaysResponse | undefined> {
        const doHolidaysSearch =
            typesArr.length === 0 ||
            typesArr.indexOf(CorpActionTypeHolidayValue) > -1;

        if (!doHolidaysSearch) {
            return Promise.resolve(undefined);
        }

        const holidaysRequest: HolidaysRequest = {
            symbols: symbols,
            symbology,
            dateSearchType,
            limit,
            lookforwardPeriod,
            lookbackPeriod,
        };

        return holidaysSearch(indigoServiceClient, holidaysRequest, options);
    }
}
